import { useState, useMemo } from 'react';
import { Button } from '@client/components/ui/button';
import { Checkbox } from '@client/components/ui/checkbox';
import { Input } from '@client/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import {
  UserMinus,
  Trash2,
  X,
  Send,
  MoreHorizontal,
  Calendar,
  RefreshCw,
  Search,
} from 'lucide-react';
import { EditSeatExpirationDialog } from './EditSeatExpirationDialog';
import { format } from 'date-fns';
import { Badge } from '@client/components/ui/badge';

type EnrichedSubscriptionSeat = SubscriptionSeat & {
  advisorEmail: string | null;
  advisorName: string | null;
};

interface SeatsTableProps {
  seats: EnrichedSubscriptionSeat[];
  subscription: any;
  isOwner: boolean;
  isMutating: boolean;
  isFetching: boolean;
  currentMutation: string | null;
  onRemoveSeat: (seatId: string) => void;
  onResendInvitation: (seatId: string, email: string) => void;
  onRefresh: () => void;
}

type SeatAction = {
  key: string;
  label: string;
  icon: typeof Trash2;
  onClick: () => void;
  disabled?: boolean;
};

export function SeatsTable({
  seats,
  subscription,
  isOwner,
  isMutating,
  isFetching,
  currentMutation,
  onRemoveSeat,
  onResendInvitation,
  onRefresh,
}: SeatsTableProps) {
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [editExpirationDialogOpen, setEditExpirationDialogOpen] = useState(false);
  const [selectedSeatForExpiration, setSelectedSeatForExpiration] =
    useState<EnrichedSubscriptionSeat | null>(null);

  const calculateMonths = (assignedDateTime: Date | null): number | string => {
    if (!assignedDateTime) return '—';
    const assignedDate = new Date(assignedDateTime);
    const today = new Date();
    const yearsDiff = today.getFullYear() - assignedDate.getFullYear();
    const monthsDiff = today.getMonth() - assignedDate.getMonth();
    const totalMonths = yearsDiff * 12 + monthsDiff;
    return Math.floor(totalMonths);
  };

  const handleOpenExpirationDialog = (seat: EnrichedSubscriptionSeat) => {
    setSelectedSeatForExpiration(seat);
    setEditExpirationDialogOpen(true);
  };

  const handleCloseExpirationDialog = () => {
    setEditExpirationDialogOpen(false);
    setSelectedSeatForExpiration(null);
  };

  const getSeatActions = (seat: EnrichedSubscriptionSeat): SeatAction[] => {
    const actions: SeatAction[] = [];
    // Add edit expiration for active and invited seats (only if feature flag is enabled)
    if (
      seat.advisorId !== subscription?.ownerAdvisorId &&
      (seat.status === 'active' || seat.status === 'invited')
    ) {
      actions.push({
        key: 'edit-expiration',
        label: 'Edit expiration',
        icon: Calendar,
        onClick: () => handleOpenExpirationDialog(seat),
      });
    }

    if (seat.status === 'inactive' && !seat.advisorId) {
      actions.push({
        key: 'remove-inactive',
        label: 'Remove seat',
        icon: Trash2,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    if (seat.status === 'invited') {
      actions.push(
        {
          key: 'resend-invitation',
          label: 'Resend invitation',
          icon: Send,
          onClick: () => onResendInvitation(seat.subscriptionSeatId, seat.invitedEmail || ''),
          disabled: isMutating && currentMutation === 'invite',
        },
        {
          key: 'cancel-invitation',
          label: 'Cancel invitation',
          icon: X,
          onClick: () => onRemoveSeat(seat.subscriptionSeatId),
        },
      );
    }

    if (seat.status === 'payment_failed') {
      actions.push({
        key: 'remove-failed',
        label: 'Remove failed seat',
        icon: X,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    if (seat.status === 'active' && seat.advisorId !== subscription?.ownerAdvisorId) {
      actions.push({
        key: 'remove-active',
        label: 'Remove seat',
        icon: UserMinus,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    return actions;
  };

  const sortedSeats = useMemo(() => {
    return seats
      .filter(seat => {
        // Status filter
        const statusMatch =
          showAllStatuses ||
          seat.status === 'invited' ||
          seat.status === 'active' ||
          seat.status === 'payment_failed' ||
          seat.status === 'paused';
        if (!statusMatch) return false;

        // Search filter
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
          seat.advisorName?.toLowerCase().includes(search) ||
          seat.advisorEmail?.toLowerCase().includes(search) ||
          seat.invitedEmail?.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        // First sort by status: active comes first
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;

        // Then sort by assignedDateTime: most recent first
        const dateA = a.assignedDateTime ? new Date(a.assignedDateTime).getTime() : 0;
        const dateB = b.assignedDateTime ? new Date(b.assignedDateTime).getTime() : 0;
        return dateB - dateA;
      });
  }, [seats, showAllStatuses, searchText]);

  return (
    <div className="space-y-4">
      {/* Search Input and Status Filter */}
      <div className="flex items-center justify-between">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder="Search seats..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="pl-10 pr-9"
          />
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isFetching}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-all-statuses"
            checked={showAllStatuses}
            onCheckedChange={checked => setShowAllStatuses(checked === true)}
          />
          <label
            htmlFor="show-all-statuses"
            className="cursor-pointer text-sm leading-none text-gray-700 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show all statuses
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="max-h-[400px] overflow-auto rounded-lg border border-gray-200 bg-white">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Assigned Date</TableHead>
              <TableHead>Months Active</TableHead>
              <TableHead>Expiration Date</TableHead>
              {isOwner && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSeats.map(seat => (
              <TableRow key={seat.subscriptionSeatId}>
                <TableCell>
                  <span
                    className={`text-xs font-medium ${
                      seat.status === 'active'
                        ? 'text-green-700'
                        : seat.status === 'invited'
                          ? 'text-gray-700'
                          : seat.status === 'invite_declined' || seat.status === 'payment_failed'
                            ? 'text-red-700'
                            : 'text-gray-700'
                    }`}
                  >
                    {seat.status.includes('_') ? seat.status.split('_').join(' ') : seat.status}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{seat.advisorName || '—'}</span>
                      {seat.advisorId === subscription?.ownerAdvisorId && (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal"
                        >
                          Owner
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-gray-700">
                      {seat.advisorEmail || seat.invitedEmail || '—'}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-gray-900">
                  {seat.assignedDateTime
                    ? format(new Date(seat.assignedDateTime), 'M/d/yyyy')
                    : '—'}
                </TableCell>
                <TableCell className="text-gray-900">
                  {calculateMonths(seat.assignedDateTime)}
                </TableCell>
                <TableCell className="text-gray-900">
                  {seat.expiresDateTime ? format(new Date(seat.expiresDateTime), 'M/d/yyyy') : '—'}
                </TableCell>
                {isOwner && (
                  <TableCell className="text-right">
                    {(() => {
                      const actions = getSeatActions(seat);
                      if (actions.length === 0) return null;

                      return (
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-gray-700 hover:text-gray-900"
                            >
                              <MoreHorizontal className="h-4 w-4 text-gray-700" />
                              <span className="sr-only">Open actions menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {actions.map(action => {
                              const Icon = action.icon;
                              return (
                                <DropdownMenuItem
                                  key={action.key}
                                  onClick={action.onClick}
                                  disabled={action.disabled}
                                >
                                  <Icon className="mr-2 h-4 w-4" />
                                  {action.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {selectedSeatForExpiration && (
        <EditSeatExpirationDialog
          open={editExpirationDialogOpen}
          onClose={handleCloseExpirationDialog}
          seat={selectedSeatForExpiration}
          initialExpiresDateTime={selectedSeatForExpiration.expiresDateTime}
        />
      )}
    </div>
  );
}
